import React, { useCallback, useEffect, useMemo, useState } from "react";
import { json, LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData, useNavigate } from "@remix-run/react";
import { Accordion, AccordionItem, Select, SelectItem } from "@heroui/react";
import { BiFilter } from "react-icons/bi";
import { parse } from "cookie";
import { findHomePageComponentsByTitlePaginated } from "../../service/homepage-component.server";
import { ProductCard } from "../../components/Product/ProductCard";
import { ProductObject } from "../../api/product/product.type";
import TablePagination from "app/components/TablePagination";
import { sortOrder } from "app/atoms/utils.atom";
import { sortOptions } from "app/utils/sortOptions";
import { useAtom } from "jotai";
import { setPageLimit } from "app/utils/fn";
import { storageKey } from "app/utils/storageKeys";
import type { SetStateAction } from "react";

export async function loader({ params, request }: LoaderFunctionArgs) {
  const { slug } = params;

  if (!slug) {
    throw new Response("Slug parameter is required", { status: 400 });
  }

  const url = new URL(request.url);
  const page = parseInt(url.searchParams.get("page") || "1", 10);
  const limit = parseInt(url.searchParams.get("limit") || "16", 10);
  const cookies = parse(request.headers.get("Cookie") || "");
  const sort =
    url.searchParams.get("sort") || cookies.sortOrder || "lowest-price";
  const resolvedLimit = parseInt(
    url.searchParams.get("limit") || cookies.pageLimit || "16",
    10
  );

  try {
    const result = await findHomePageComponentsByTitlePaginated(slug, {
      page,
      limit: resolvedLimit,
      sort,
    });

    return json({
      success: true,
      slug,
      ...result,
      sort,
    });
  } catch (error) {
    console.error("Error loading homepage components:", error);
    return json({
      success: false,
      docs: [],
      components: [],
      slug,
      page: 1,
      limit: resolvedLimit,
      totalItems: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
      nextPage: 0,
      prevPage: 0,
      sort,
      error: "Failed to load products",
    });
  }
}

export default function PromotionalRoute() {
  const loaderData = useLoaderData<typeof loader>();
  const navigate = useNavigate();
  const [currentSortOrder, setCurrentSortOrder] = useAtom(sortOrder);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(16);

  const {
    success,
    slug,
    docs: products = [],
    components = [],
    page,
    limit: loaderLimit,
    totalPages,
    totalItems,
    hasNextPage,
    hasPrevPage,
    nextPage,
    prevPage,
    sort: loaderSort = "lowest-price",
  } = loaderData;

  const error =
    "error" in loaderData ? (loaderData.error as string) : undefined;

  const filteredProducts = useMemo(
    () => ({
      products,
      page,
      limit: loaderLimit,
      totalPages: totalPages ?? 0,
      totalItems: totalItems ?? 0,
      hasNext: hasNextPage ?? false,
      hasPrevious: hasPrevPage ?? false,
      nextPage: nextPage ?? 0,
      prevPage: prevPage ?? 0,
      sort: loaderSort,
    }),
    [
      products,
      page,
      loaderLimit,
      totalPages,
      totalItems,
      hasNextPage,
      hasPrevPage,
      nextPage,
      prevPage,
      loaderSort,
    ]
  );

  useEffect(() => {
    if (loaderSort && loaderSort !== currentSortOrder) {
      setCurrentSortOrder(loaderSort);
    }
  }, [loaderSort, currentSortOrder, setCurrentSortOrder]);

  useEffect(() => {
    if (typeof window !== "undefined" && localStorage) {
      const _limit = localStorage.getItem(storageKey.pageLimit) || "16";
      setLimit(Number(_limit));
    }
  }, []);

  const updateQueryParams = useCallback(
    ({
      page: p,
      limit: l,
      sort: s,
    }: {
      page: number;
      limit: number;
      sort?: string;
    }) => {
      const params = new URLSearchParams();
      params.set("page", String(p));
      params.set("limit", String(l));
      if (s) params.set("sort", s);
      navigate(`?${params.toString()}`);
    },
    [navigate]
  );

  const handleNext = useCallback(
    (pageNumber: number) => {
      if (filteredProducts?.hasNext && !pageNumber) {
        updateQueryParams({
          page: filteredProducts.nextPage,
          limit,
          sort: currentSortOrder,
        });
        setCurrentPage(filteredProducts.nextPage);
      } else {
        const p = pageNumber || filteredProducts.nextPage;
        updateQueryParams({ page: p, limit, sort: currentSortOrder });
        setCurrentPage(p);
      }
    },
    [
      filteredProducts.hasNext,
      filteredProducts.nextPage,
      limit,
      currentSortOrder,
      updateQueryParams,
    ]
  );

  const handlePrevious = useCallback(() => {
    if (filteredProducts.hasPrevious) {
      updateQueryParams({
        page: filteredProducts.prevPage,
        limit,
        sort: currentSortOrder,
      });
      setCurrentPage(filteredProducts.prevPage);
    }
  }, [
    filteredProducts.hasPrevious,
    filteredProducts.prevPage,
    limit,
    currentSortOrder,
    updateQueryParams,
  ]);

  const handleChangeLimit = useCallback(
    (newLimit: SetStateAction<number>) => {
      const value = typeof newLimit === "function" ? newLimit(limit) : newLimit;
      setLimit(value);
      setPageLimit(value);
      updateQueryParams({
        page: currentPage,
        limit: value,
        sort: currentSortOrder,
      });
    },
    [currentPage, currentSortOrder, limit, updateQueryParams]
  );

  const handleSortChange = useCallback(
    (keys: Iterable<unknown> | Array<unknown>) => {
      const newSort = Array.from(keys as Iterable<string>).join(",");
      setCurrentSortOrder(newSort);
      updateQueryParams({
        page: currentPage,
        limit,
        sort: newSort,
      });
    },
    [currentPage, limit, updateQueryParams, setCurrentSortOrder]
  );

  if (!success) {
    return (
      <div className="container mx-auto px-4 py-8 bg-white dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
            Error
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            {error || "An unknown error occurred"}
          </p>
        </div>
      </div>
    );
  }

  if (products.length === 0 && totalItems === 0) {
    return (
      <div className="container mx-auto px-4 py-8 bg-white dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            No Products found for &quot;{slug}&quot;
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            We couldn&apos;t find any products in the promotional section
            &quot;{slug}&quot;.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-white dark:bg-gray-900">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
          Products in &quot;{components?.[0]?.title}&quot;
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          {filteredProducts.totalItems} product
          {filteredProducts.totalItems !== 1 ? "s" : ""} across {components.length}{" "}
          promotional section{components.length !== 1 ? "s" : ""}
        </p>
      </div>

      <Accordion className="bg-zinc-50 dark:bg-zinc-800 rounded-xl mb-6">
        <AccordionItem
          key="sort"
          aria-label="Sort"
          subtitle="Press to expand"
          title={
            <div className="flex space-x-3 items-center">
              <BiFilter size={24} className="text-primary" />
              <span className="text-foreground md:text-2xl font-semibold text-center">
                Sort
              </span>
            </div>
          }
        >
          <div className="justify-end w-full md:w-2/5">
            <div className="flex items-end justify-between space-x-3 mb-2 w-full">
              <Select
                variant="bordered"
                label="Sort by: Price"
                labelPlacement="inside"
                className="max-w-xs text-center"
                placeholder=""
                classNames={{
                  trigger: ["border-zinc-100 rounded-md py-1"],
                  label: ["text-sm w-28"],
                  popoverContent: "w-64",
                }}
                items={sortOptions}
                size="sm"
                selectedKeys={[currentSortOrder]}
                onSelectionChange={handleSortChange}
              >
                {(option) => <SelectItem key={option.key}>{option.label}</SelectItem>}
              </Select>
            </div>
          </div>
        </AccordionItem>
      </Accordion>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-6">
        {products.map((product: ProductObject) => {
          if (!product) return null;
          return (
            <ProductCard
              key={product._id}
              isSpecialSupplier={product?.supplier?.isSpecialSupplier ?? false}
              labels={product?.labels || []}
              image={product?.overview?.heroImage || ""}
              images={product?.product?.images || []}
              title={product?.overview?.name || ""}
              productCode={product?.overview?.code || ""}
              description={product?.product?.description || ""}
              userTypePrice={product?.userTypePrice || {}}
              qty={product?.quantity || { min: 0, max: 0 }}
              slug={product?.slug || ""}
              category={
                product?.category?.name ||
                product?.product?.categorisation?.productType?.typeName ||
                ""
              }
              subCatgory={
                product?.subCategory?.name ||
                product?.product?.categorisation?.supplierSubCategory ||
                ""
              }
            />
          );
        })}
      </div>

      {filteredProducts.totalPages > 1 && (
        <div className="flex w-full justify-center px-5 py-6">
          <TablePagination
            totalPages={filteredProducts.totalPages}
            currentPage={filteredProducts.page}
            handlePrevious={handlePrevious}
            handleNext={(p) => handleNext(p as number)}
            setLimit={handleChangeLimit}
          />
        </div>
      )}
    </div>
  );
}
